"use client";

import { Box, Flex, Heading } from "@chakra-ui/react";
import { RichText } from "@graphcms/rich-text-react-renderer";
import { RichTextContent } from "@graphcms/rich-text-types";
import { Button, Button_T } from "@ui/components";
import { SectionTemplate } from "components/individuals/blocks";
import { customRichText } from "components/individuals/mdx-components";
import { ButtonProp, ImageFragment } from "lib/gcms";
import Image from "next/image";

export interface ClientReportsCtaSectionProps {
  bg: string;
  textColor: string;
  image: ImageFragment & { alt?: string };
  content: {
    raw: RichTextContent;
  };
  title: string;
  subtitle: string;
  buttons?: Omit<ButtonProp, "buttonAction" | "id">[];
}

export const ClientReportsCtaSection = (
  props: ClientReportsCtaSectionProps,
) => {
  return (
    <SectionTemplate bg={props.bg} color={props.textColor}>
      <Heading
        as={"h1"}
        fontSize={{
          base: 26,
          md: 34,
          lg: 38,
          xl: 45,
        }}
        lineHeight={1.2}
        fontWeight={"bold"}
        mb={{
          base: 8,
          lg: 12,
        }}
        textAlign={"left"}
        w={{
          lg: "80%",
        }}
      >
        {props.title}
      </Heading>
      <Heading
        as={"h2"}
        fontSize={{
          base: 16,
          md: 20,
          xl: 22,
        }}
        fontWeight={"semibold"}
        mb={{
          base: 0,
          lg: 14,
        }}
        textAlign={"left"}
        w={{
          lg: "85%",
        }}
      >
        {props.subtitle}
      </Heading>

      <Flex
        flexDir={{
          base: "column",
          md: "row",
        }}
        gap={10}
      >
        <Box
          mt={{
            base: 10,
            md: 0,
          }}
          maxW={{
            base: "350px",
            md: "550px",
          }}
          mx="auto"
          flex={1}
        >
          <Image
            {...props.image}
            loading="lazy"
            src={props.image?.url as string}
            alt={props.image?.alt as string}
            height={props.image?.height}
            width={props.image?.width}
            placeholder={
              typeof props.image?.blurDataURL === "string" ? "blur" : undefined
            }
            blurDataURL={
              typeof props.image?.blurDataURL === "string"
                ? props.image?.blurDataURL
                : undefined
            }
          />
        </Box>
        <Box flex={1}>
          <RichText
            content={props.content?.raw as RichTextContent}
            renderers={customRichText(props.textColor, undefined, undefined, {
              base: 17,
              lg: 19,
              xl: 21,
            })}
          />
          <Flex
            mt={{
              base: 10,
            }}
            justify={"center"}
          >
            {props.buttons?.map((btn, index) => (
              <Button
                key={`report-cta-btn-${index}`}
                bgColor={props.textColor}
                color={props.bg}
                fontSize={{
                  base: 12,
                  md: 14,
                  lg: 16,
                  xl: 18,
                }}
                fontWeight={"bold"}
                py={{
                  base: "6",
                  lg: "8",
                }}
                w={{
                  base: "140px",
                  md: "160px",
                  lg: "180px",
                  xl: "220px",
                }}
                textTransform={"uppercase"}
                {...(btn as Button_T)}
              />
            ))}
          </Flex>
        </Box>
      </Flex>
    </SectionTemplate>
  );
};
